import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Button,
  CloseButton,
  Modal,
  Form,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import {
  useArchiveMasterAccountMutation,
  useLazyGetClientRestoreEligibilityQuery,
  useListMasterAccountsQuery,
  useListMasterAccountBrandsQuery,
  usePermanentDeleteMasterAccountMutation,
  useResetMasterAccountPinMutation,
  useRestoreMasterAccountMutation,
  useSetMasterAccountPinMutation,
  useLazyGetMasterAccountPinQuery,
  useStartImpersonationMutation,
  useUpdateMasterAccountMutation,
  useUpdateOwnerProfileMutation,
  useValidateSupportTicketMutation,
  useReserveSupportTicketMutation,
  useCloseSupportTicketMutation,
  useListSupportTicketsQuery,
  type BrandMembershipRow,
  type MasterAccountRow,
  type SupportTicketRow,
} from "@/features/accounts/accountsApi";
import { parseAccountsDeepLink, stripAccountsDeepLinkParams } from "@/features/accounts/accountsDeepLink";
import {
  findClientHits,
  type FindClientMatch,
} from "@/features/accounts/clientSupportLookup";
import { filterOpenTicketsForSearch } from "@/features/accounts/supportModeReference";
import {
  formatClientCaseHeading,
  formatSupportStatusLabel,
  isOpenSupportStatus,
  isClosedSupportStatus,
} from "@/features/accounts/components/SupportCasesPanel";
import OwnerAccountPanel from "@/features/accounts/components/OwnerAccountPanel";
import {
  DetailField,
  DetailGrid,
  DetailSection,
} from "@/features/accounts/components/ClientDetailsFields";
import {
  formatClientAddressLines,
  formatClientDetailsDate,
} from "@/features/accounts/clientDetailsLayout";
import styles from "@/pages/accounts.module.scss";
import {
  CLIENT_LIFECYCLE_MIN_REASON_LENGTH,
  CLIENT_PERMANENT_DELETE_CONFIRM_TOKEN,
  archiveOperatorImpactLines,
  artifactIdsForRestore,
  canSubmitClientArchive,
  canSubmitClientPermanentDelete,
  clientArchivePreflightFromError,
  clientPreflightFromError,
  defaultSelectedArtifactIds,
  extractApiErrorMessage,
  formatClientArtifactLabel,
  groupArtifactsByType,
  toggleArtifactSelection,
  type ClientArchivePreflight,
  type ClientLifecycleView,
  type ClientPermanentDeletePreflight,
} from "@/features/accounts/clientLifecycle";
import { formatUsPhoneInput } from "@/utils/phoneFormat";
import { SONG_LIBRARY_ACTION_ICONS } from "@/styles/songLibraryActionIcons";
import { useCanPerformLifecycleDelete } from "@/contexts/SuperAdminIdentityContext";
import { NESTED_RESOLVE_STACK } from "@/features/accounts/nestedResolveModalStack";
import SupportCasesPanel from "@/features/accounts/components/SupportCasesPanel";

interface AccountsListProps {
  searchTerm?: string;
  filters?: {
    clientId?: string;
    companyName?: string;
    status?: string;
    /** Free-text support identification (name, email, company, customer #). */
    findClient?: string;
    /** Brand / Store # lookup scope; null/undefined = no Location-based narrowing. */
    masterAccountIds?: string[] | null;
  };
  view?: ClientLifecycleView;
  onViewChange?: (view: ClientLifecycleView) => void;
}

type EditFormState = {
  businessName: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_zipCode: string;
  address_country: string;
  billingPhone: string;
  billingEmail: string;
  active: boolean;
  ownerFirstName: string;
  ownerLastName: string;
  ownerPhone: string;
};

function emptyEditForm(): EditFormState {
  return {
    businessName: "",
    address_street: "",
    address_city: "",
    address_state: "",
    address_zipCode: "",
    address_country: "US",
    billingPhone: "",
    billingEmail: "",
    active: true,
    ownerFirstName: "",
    ownerLastName: "",
    ownerPhone: "",
  };
}

/** Stable empty list for brand-query skip/loading — never allocate `|| []` per render. */
const EMPTY_CLIENT_BRANDS: BrandMembershipRow[] = [];

function formFromAccount(account: MasterAccountRow): EditFormState {
  return {
    businessName: account.businessName || "",
    address_street: account.address_street || "",
    address_city: account.address_city || "",
    address_state: account.address_state || "",
    address_zipCode: account.address_zipCode || "",
    address_country: account.address_country || "US",
    billingPhone: formatUsPhoneInput(
      account.billingPhone || account.companyNumber || "",
    ),
    billingEmail: account.billingEmail || "",
    active: Boolean(account.active),
    ownerFirstName: "",
    ownerLastName: "",
    ownerPhone: "",
  };
}

function getMaUiOrigin(): string {
  // Canonical staging MA host is staging.myaccount.krosskast.com
  // (staging.account.krosskast.com has no DNS). Production override via env.
  return (
    process.env.REACT_APP_MA_UI_ORIGIN ||
    (process.env.REACT_APP_ENV === "production"
      ? "https://myaccount.krosskast.com"
      : "https://staging.myaccount.krosskast.com")
  ).replace(/\/$/, "");
}

/** Never show Auth0 subject ids in the SA UI. */
function isAuth0Subject(value: string | null | undefined): boolean {
  return Boolean(value && /^auth0\|/i.test(value.trim()));
}

function formatOwnerLabel(
  displayName: string | null | undefined,
  email: string | null | undefined,
): string | null {
  const name =
    displayName && !isAuth0Subject(displayName) ? displayName.trim() : "";
  const mail = email && !isAuth0Subject(email) ? email.trim() : "";
  if (name && mail && name !== mail) return `${name} · ${mail}`;
  if (name) return name;
  if (mail) return mail;
  return null;
}

function pinApiErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    status?: number;
    data?: { message?: string; statusCode?: number };
    message?: string;
  };
  const status = e?.status ?? e?.data?.statusCode;
  const raw = String(e?.data?.message || e?.message || "");
  if (
    status === 404 ||
    /^not found$/i.test(raw.trim()) ||
    /cannot (GET|POST).*security-pin/i.test(raw)
  ) {
    return "Security PIN reveal is not available on staging yet (API not deployed).";
  }
  return raw.trim() || fallback;
}

const AccountsList: React.FC<AccountsListProps> = ({
  searchTerm = "",
  filters = {},
  view = "active",
  onViewChange,
}) => {
  const canLifecycle = useCanPerformLifecycleDelete();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, refetch } =
    useListMasterAccountsQuery({ kind: "client", view });
  const [resetPin, { isLoading: resetting }] =
    useResetMasterAccountPinMutation();
  const [setPin, { isLoading: settingPin }] = useSetMasterAccountPinMutation();
  const [fetchSecurityPin] = useLazyGetMasterAccountPinQuery();
  const [startImpersonation, { isLoading: impersonating }] =
    useStartImpersonationMutation();
  const [validateSupportTicket, { isLoading: validatingTicket }] =
    useValidateSupportTicketMutation();
  const [reserveSupportTicket, { isLoading: reservingCaseId }] =
    useReserveSupportTicketMutation();
  const [closeTicket, { isLoading: closingTicket }] =
    useCloseSupportTicketMutation();
  const [updateClient, { isLoading: updating }] =
    useUpdateMasterAccountMutation();
  const [updateOwnerProfile, { isLoading: updatingOwner }] =
    useUpdateOwnerProfileMutation();
  const [archiveClient, { isLoading: archiving }] =
    useArchiveMasterAccountMutation();
  const [restoreClient, { isLoading: restoring }] =
    useRestoreMasterAccountMutation();
  const [permanentDeleteClient, { isLoading: deleting }] =
    usePermanentDeleteMasterAccountMutation();
  const [
    fetchRestoreEligibility,
    {
      data: restoreEligibility,
      isFetching: restoreEligibilityLoading,
      isError: restoreEligibilityError,
      error: restoreEligibilityErr,
    },
  ] = useLazyGetClientRestoreEligibilityQuery();

  const appliedDeepLinkRef = useRef<string | null>(null);

  const [pinTarget, setPinTarget] = useState<MasterAccountRow | null>(null);
  const [pinReason, setPinReason] = useState("");
  const [pinSetTarget, setPinSetTarget] = useState<MasterAccountRow | null>(
    null,
  );
  const [pinSetValue, setPinSetValue] = useState("");
  const [pinSetError, setPinSetError] = useState<string | null>(null);
  const [detailsTarget, setDetailsTarget] = useState<MasterAccountRow | null>(
    null,
  );
  const [detailsPinReveal, setDetailsPinReveal] = useState<{
    pinStatus: "set" | "unset";
    viewable: boolean;
    securityPin: string | null;
    message?: string;
  } | null>(null);
  const [detailsPinLoading, setDetailsPinLoading] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<SupportTicketRow | null>(
    null,
  );
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [intoCaseTarget, setIntoCaseTarget] =
    useState<SupportTicketRow | null>(null);
  const [intoCaseReason, setIntoCaseReason] = useState("");
  const [intoCaseMinutes, setIntoCaseMinutes] = useState(30);
  const [intoCaseError, setIntoCaseError] = useState<string | null>(null);
  const [impersonateTarget, setImpersonateTarget] =
    useState<MasterAccountRow | null>(null);
  const [impersonateReason, setImpersonateReason] = useState("");
  const [impersonateMinutes, setImpersonateMinutes] = useState(30);
  const [supportRefMode, setSupportRefMode] = useState<
    "existing-ticket" | "ad-hoc-created" | "maintenance"
  >("ad-hoc-created");
  const [existingTicketId, setExistingTicketId] = useState("");
  const [ticketSearch, setTicketSearch] = useState("");
  const [validatedTicketSummary, setValidatedTicketSummary] = useState<
    string | null
  >(null);
  const [persistedSupportCaseId, setPersistedSupportCaseId] = useState<
    string | null
  >(null);
  const [caseIdReserved, setCaseIdReserved] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [lockedCaseBrandId, setLockedCaseBrandId] = useState<string | null>(
    null,
  );
  const [lockedCaseBrandName, setLockedCaseBrandName] = useState<string | null>(
    null,
  );
  const { data: clientBrandsData, isFetching: clientBrandsLoading } =
    useListMasterAccountBrandsQuery(impersonateTarget?.masterAccountId || "", {
      skip: !impersonateTarget?.masterAccountId,
    });
  // Stable empty fallback — `|| []` allocates a new array every render while
  // brands are loading/undefined, which re-fires the brand-selection effect.
  const clientBrands = useMemo(
    () => clientBrandsData?.brands ?? EMPTY_CLIENT_BRANDS,
    [clientBrandsData?.brands],
  );
  const { data: openTicketsData, isFetching: openTicketsLoading } =
    useListSupportTicketsQuery(
      { masterAccountId: impersonateTarget?.masterAccountId || "" },
      {
        skip:
          !impersonateTarget?.masterAccountId ||
          supportRefMode !== "existing-ticket",
      },
    );
  const openSupportTickets = useMemo(
    () =>
      filterOpenTicketsForSearch(
        openTicketsData?.supportTickets || [],
        ticketSearch,
        isOpenSupportStatus,
      ),
    [openTicketsData?.supportTickets, ticketSearch],
  );
  const [detailsEditMode, setDetailsEditMode] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>(emptyEditForm);
  const [ownerFieldsHydrated, setOwnerFieldsHydrated] = useState(false);
  const [ownerRefreshToken, setOwnerRefreshToken] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);

  const [archiveTarget, setArchiveTarget] = useState<MasterAccountRow | null>(
    null,
  );
  const [archiveNameConfirmation, setArchiveNameConfirmation] = useState("");
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archivePreflight, setArchivePreflight] =
    useState<ClientArchivePreflight | null>(null);
  const [archivePreflightLoading, setArchivePreflightLoading] = useState(false);

  const [restoreTarget, setRestoreTarget] = useState<MasterAccountRow | null>(
    null,
  );
  const [selectedArtifactIds, setSelectedArtifactIds] = useState<string[]>([]);
  const [restoreNotes, setRestoreNotes] = useState("");
  const [restoreConfirmed, setRestoreConfirmed] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const [pdTarget, setPdTarget] = useState<MasterAccountRow | null>(null);
  const [pdPreflight, setPdPreflight] =
    useState<ClientPermanentDeletePreflight | null>(null);
  const [pdPhase, setPdPhase] = useState<
    "idle" | "preflight" | "confirm" | "running" | "error"
  >("idle");
  const [pdNameConfirmation, setPdNameConfirmation] = useState("");
  const [pdConfirmTyped, setPdConfirmTyped] = useState("");
  const [pdReason, setPdReason] = useState("");
  const [pdError, setPdError] = useState<string | null>(null);

  // Find Client is a pure search over the list projection (canonical owner
  // first/last name, owner email, billing email, company, customer #).
  const findClientQuery = String(filters.findClient || "").trim();

  const { accounts, findClientMatches } = useMemo(() => {
    const rows = data?.masterAccounts || [];
    const scope = filters.masterAccountIds
      ? new Set(filters.masterAccountIds)
      : null;
    const base = rows.filter((account) => {
      const matchesSearch = account.businessName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesClientId =
        !filters.clientId ||
        account.customerNumber === filters.clientId ||
        account.clientId === filters.clientId ||
        account.masterAccountId === filters.clientId;
      const matchesCompanyName =
        !filters.companyName || account.businessName === filters.companyName;
      const matchesStatus =
        !filters.status ||
        (filters.status === "Active" && account.active) ||
        (filters.status === "Inactive" && !account.active);
      const matchesScope = !scope || scope.has(account.masterAccountId);
      return (
        matchesSearch &&
        matchesClientId &&
        matchesCompanyName &&
        matchesStatus &&
        matchesScope
      );
    });
    const hits = findClientHits(base, findClientQuery);
    const matchMap = new Map<string, FindClientMatch[]>();
    for (const hit of hits) matchMap.set(hit.account.masterAccountId, hit.matches);
    return {
      accounts: hits.map((h) => h.account),
      findClientMatches: matchMap,
    };
  }, [data, searchTerm, filters, findClientQuery]);

  const totalPages = Math.max(1, Math.ceil(accounts.length / 25));
  const pageRows = accounts.slice((page - 1) * 25, page * 25);

  useEffect(() => {
    setPage(1);
  }, [view]);

  useEffect(() => {
    if (!impersonateTarget) {
      setSelectedBrandId("");
      return;
    }
    if (lockedCaseBrandId) {
      setSelectedBrandId(lockedCaseBrandId);
      return;
    }
    if (
      (supportRefMode === "ad-hoc-created" ||
        supportRefMode === "maintenance") &&
      clientBrands.length === 1
    ) {
      setSelectedBrandId(clientBrands[0].brandId);
      return;
    }
    if (
      selectedBrandId &&
      !clientBrands.some((b) => b.brandId === selectedBrandId)
    ) {
      setSelectedBrandId("");
    }
  }, [
    impersonateTarget,
    lockedCaseBrandId,
    clientBrands,
    supportRefMode,
    selectedBrandId,
  ]);

  const setEditField = <K extends keyof EditFormState>(
    key: K,
    value: EditFormState[K],
  ) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const enterDetailsEditMode = (account: MasterAccountRow) => {
    setActionError(null);
    setDetailsEditMode(true);
    setEditForm(formFromAccount(account));
    setOwnerFieldsHydrated(false);
  };

  const cancelDetailsEdit = () => {
    setDetailsEditMode(false);
    setEditForm(emptyEditForm());
    setOwnerFieldsHydrated(false);
    setActionError(null);
  };

  const handleOwnerHydrated = useCallback(
    (profile: {
      firstName: string | null;
      lastName: string | null;
      phone: string | null;
      email: string | null;
    }) => {
      if (!detailsEditMode || ownerFieldsHydrated) return;
      setEditForm((prev) => ({
        ...prev,
        ownerFirstName: profile.firstName || "",
        ownerLastName: profile.lastName || "",
        ownerPhone: formatUsPhoneInput(profile.phone || ""),
      }));
      setOwnerFieldsHydrated(true);
    },
    [detailsEditMode, ownerFieldsHydrated],
  );

  const setOwnerEditField = <K extends "firstName" | "lastName" | "phone">(
    key: K,
    value: string,
  ) => {
    const map = {
      firstName: "ownerFirstName",
      lastName: "ownerLastName",
      phone: "ownerPhone",
    } as const;
    setEditField(map[key], value);
  };

  const handleSaveDetailsEdit = async () => {
    if (!detailsTarget) return;
    if (!editForm.businessName.trim()) {
      setActionError("Company name is required");
      return;
    }
    setActionError(null);
    const masterAccountId = detailsTarget.masterAccountId;
    let companyError: string | null = null;
    let ownerError: string | null = null;
    let companySaved = false;
    let ownerSaved = false;
    let updatedAccount: Partial<MasterAccountRow> | null = null;

    try {
      const result = await updateClient({
        masterAccountId,
        businessName: editForm.businessName.trim(),
        address_street: editForm.address_street.trim(),
        address_city: editForm.address_city.trim(),
        address_state: editForm.address_state.trim(),
        address_zipCode: editForm.address_zipCode.trim(),
        address_country: editForm.address_country.trim() || "US",
        billingPhone: editForm.billingPhone.trim() || undefined,
        billingEmail: editForm.billingEmail.trim() || undefined,
        active: editForm.active,
      }).unwrap();
      companySaved = true;
      updatedAccount = result.masterAccount || null;
    } catch (err: any) {
      companyError =
        err?.data?.message || err?.message || "Company update failed";
    }

    try {
      await updateOwnerProfile({
        masterAccountId,
        firstName: editForm.ownerFirstName.trim(),
        lastName: editForm.ownerLastName.trim(),
        phone: editForm.ownerPhone.trim() || undefined,
      }).unwrap();
      ownerSaved = true;
    } catch (err: any) {
      ownerError =
        err?.data?.message || err?.message || "Owner profile update failed";
    }

    if (companySaved && updatedAccount) {
      const savedAccount = updatedAccount;
      setDetailsTarget((prev) =>
        prev
          ? {
              ...prev,
              ...savedAccount,
              businessName:
                savedAccount.businessName || editForm.businessName.trim(),
            }
          : prev,
      );
    } else if (companySaved) {
      setDetailsTarget((prev) =>
        prev
          ? {
              ...prev,
              businessName: editForm.businessName.trim(),
              address_street: editForm.address_street.trim(),
              address_city: editForm.address_city.trim(),
              address_state: editForm.address_state.trim(),
              address_zipCode: editForm.address_zipCode.trim(),
              address_country: editForm.address_country.trim() || "US",
              billingPhone: editForm.billingPhone.trim(),
              billingEmail: editForm.billingEmail.trim(),
              active: editForm.active,
            }
          : prev,
      );
    }

    if (ownerSaved) {
      setOwnerRefreshToken((n) => n + 1);
    }

    if (companySaved && ownerSaved) {
      setDetailsEditMode(false);
      setEditForm(emptyEditForm());
      setOwnerFieldsHydrated(false);
      setActionError(null);
      return;
    }

    const parts: string[] = [];
    if (companyError) parts.push(`Company: ${companyError}`);
    if (ownerError) parts.push(`Owner profile: ${ownerError}`);
    if (companySaved && !ownerSaved) {
      parts.unshift("Company saved; owner profile did not save.");
    } else if (!companySaved && ownerSaved) {
      parts.unshift("Owner profile saved; company did not save.");
    }
    setActionError(parts.join(" "));
  };

  const closeArchiveModal = () => {
    setArchiveTarget(null);
    setArchiveNameConfirmation("");
    setArchiveReason("");
    setArchiveError(null);
    setArchivePreflight(null);
    setArchivePreflightLoading(false);
  };

  const openArchiveModal = async (account: MasterAccountRow) => {
    if (!canLifecycle) return;
    setArchiveTarget(account);
    setArchiveNameConfirmation("");
    setArchiveReason("");
    setArchiveError(null);
    setArchivePreflight(null);
    setArchivePreflightLoading(true);
    try {
      const result = await archiveClient({
        masterAccountId: account.masterAccountId,
        dryRun: true,
      }).unwrap();
      if ("preflight" in result && result.preflight) {
        setArchivePreflight(result.preflight);
        if (!result.preflight.eligible) {
          setArchiveError(
            result.preflight.blockers?.[0]?.message ||
              "Archive impact could not be determined; Archive is blocked.",
          );
        }
      } else {
        setArchiveError("Archive preflight response missing impact summary");
      }
    } catch (err) {
      const fromConflict = clientArchivePreflightFromError(err);
      if (fromConflict) {
        setArchivePreflight(fromConflict);
        setArchiveError(
          fromConflict.eligible
            ? null
            : extractApiErrorMessage(
                err,
                "Archive impact could not be determined; Archive is blocked.",
              ),
        );
      } else {
        setArchiveError(
          extractApiErrorMessage(
            err,
            "Could not load authoritative Archive impact. Archive is blocked.",
          ),
        );
      }
    } finally {
      setArchivePreflightLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    if (
      !canSubmitClientArchive({
        businessName: archiveTarget.businessName,
        nameConfirmation: archiveNameConfirmation,
        reason: archiveReason,
      })
    ) {
      setArchiveError(
        `Type the exact company name and a reason (min ${CLIENT_LIFECYCLE_MIN_REASON_LENGTH} characters).`,
      );
      return;
    }
    setArchiveError(null);
    try {
      await archiveClient({
        masterAccountId: archiveTarget.masterAccountId,
        nameConfirmation: archiveNameConfirmation,
        reason: archiveReason.trim(),
      }).unwrap();
      toast.success(`"${archiveTarget.businessName}" archived`);
      closeArchiveModal();
      onViewChange?.("archived");
    } catch (err) {
      setArchiveError(extractApiErrorMessage(err, "Archive failed"));
    }
  };

  const closeRestoreModal = () => {
    setRestoreTarget(null);
    setSelectedArtifactIds([]);
    setRestoreNotes("");
    setRestoreConfirmed(false);
    setRestoreError(null);
  };

  const openRestoreModal = async (account: MasterAccountRow) => {
    if (!canLifecycle) return;
    setRestoreTarget(account);
    setSelectedArtifactIds([]);
    setRestoreNotes("");
    setRestoreConfirmed(false);
    setRestoreError(null);
    try {
      const result = await fetchRestoreEligibility(
        account.masterAccountId,
      ).unwrap();
      setSelectedArtifactIds(
        defaultSelectedArtifactIds(result.eligibleArtifacts || []),
      );
    } catch (err) {
      setRestoreError(
        extractApiErrorMessage(err, "Failed to load eligible artifacts"),
      );
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget || !restoreConfirmed) return;
    setRestoreError(null);
    try {
      const artifactIds = artifactIdsForRestore(selectedArtifactIds);
      await restoreClient({
        masterAccountId: restoreTarget.masterAccountId,
        artifactIds,
        notes: restoreNotes.trim() || undefined,
      }).unwrap();
      toast.success(
        `"${restoreTarget.businessName}" restored${
          artifactIds.length
            ? ` · ${artifactIds.length} access item(s) restored`
            : " · no access restored"
        }`,
      );
      closeRestoreModal();
      onViewChange?.("active");
    } catch (err) {
      setRestoreError(extractApiErrorMessage(err, "Restore failed"));
    }
  };

  const closePdModal = () => {
    setPdTarget(null);
    setPdPreflight(null);
    setPdPhase("idle");
    setPdNameConfirmation("");
    setPdConfirmTyped("");
    setPdReason("");
    setPdError(null);
  };

  const openPdModal = async (account: MasterAccountRow) => {
    if (!canLifecycle) return;
    setPdTarget(account);
    setPdPreflight(null);
    setPdNameConfirmation("");
    setPdConfirmTyped("");
    setPdReason("");
    setPdError(null);
    setPdPhase("preflight");
    try {
      // dryRun skips typed-confirm validation; always returns preflight when archived.
      const result = await permanentDeleteClient({
        masterAccountId: account.masterAccountId,
        nameConfirmation: "",
        confirm: CLIENT_PERMANENT_DELETE_CONFIRM_TOKEN,
        reason: "",
        dryRun: true,
      }).unwrap();
      if ("preflight" in result && result.preflight) {
        setPdPreflight(result.preflight);
        setPdPhase("confirm");
      } else {
        setPdError("Preflight response missing eligibility summary");
        setPdPhase("error");
      }
    } catch (err) {
      const fromConflict = clientPreflightFromError(err);
      if (fromConflict) {
        setPdPreflight(fromConflict);
        setPdPhase("confirm");
        setPdError(
          fromConflict.eligible
            ? null
            : extractApiErrorMessage(err, "Permanent delete is blocked"),
        );
      } else {
        setPdError(
          extractApiErrorMessage(err, "Could not load deletion preflight"),
        );
        setPdPhase("error");
      }
    }
  };

  const handlePermanentDelete = async () => {
    if (!pdTarget || !pdPreflight?.eligible) return;
    if (
      !canSubmitClientPermanentDelete({
        businessName: pdTarget.businessName,
        nameConfirmation: pdNameConfirmation,
        confirmTyped: pdConfirmTyped,
        reason: pdReason,
        eligible: pdPreflight.eligible,
      })
    ) {
      setPdError(
        `Type the exact company name, ${CLIENT_PERMANENT_DELETE_CONFIRM_TOKEN}, and a reason (min ${CLIENT_LIFECYCLE_MIN_REASON_LENGTH} characters).`,
      );
      return;
    }
    setPdError(null);
    setPdPhase("running");
    try {
      await permanentDeleteClient({
        masterAccountId: pdTarget.masterAccountId,
        nameConfirmation: pdNameConfirmation,
        confirm: CLIENT_PERMANENT_DELETE_CONFIRM_TOKEN,
        reason: pdReason.trim(),
      }).unwrap();
      toast.success(`"${pdTarget.businessName}" permanently deleted`);
      closePdModal();
    } catch (err) {
      const conflictPreflight = clientPreflightFromError(err);
      if (conflictPreflight) setPdPreflight(conflictPreflight);
      setPdError(extractApiErrorMessage(err, "Permanent delete failed"));
      setPdPhase("error");
    }
  };

  const handleResetPin = async () => {
    if (!pinTarget) return;
    setActionError(null);
    const account = pinTarget;
    try {
      await resetPin({
        masterAccountId: account.masterAccountId,
        reason: pinReason,
      }).unwrap();
      setPinTarget(null);
      setPinReason("");
      if (detailsTarget?.masterAccountId === account.masterAccountId) {
        setDetailsTarget({ ...detailsTarget, pinStatus: "unset" });
        setDetailsPinReveal({
          pinStatus: "unset",
          viewable: false,
          securityPin: null,
        });
      }
      // Continue straight into Set PIN — do not make the operator re-find the key.
      setPinSetTarget({ ...account, pinStatus: "unset" });
      setPinSetValue("");
      setPinSetError(null);
      toast.success("PIN cleared — enter a new 4-digit PIN");
    } catch (err: any) {
      setActionError(err?.data?.message || err?.message || "Reset PIN failed");
    }
  };

  const closeSetPin = () => {
    setPinSetTarget(null);
    setPinSetValue("");
    setPinSetError(null);
  };

  const handleSetPin = async () => {
    if (!pinSetTarget) return;
    const securityPin = pinSetValue.trim();
    if (!/^\d{4}$/.test(securityPin)) {
      setPinSetError("PIN must be exactly 4 digits");
      return;
    }
    setPinSetError(null);
    const account = pinSetTarget;
    try {
      await setPin({
        masterAccountId: account.masterAccountId,
        securityPin,
      }).unwrap();
      toast.success("Security PIN saved");
      closeSetPin();
      // Show the new PIN in Client details immediately.
      setDetailsTarget({ ...account, pinStatus: "set" });
      setDetailsPinReveal({
        pinStatus: "set",
        viewable: true,
        securityPin,
      });
      setDetailsPinLoading(false);
    } catch (err: unknown) {
      setPinSetError(pinApiErrorMessage(err, "Set PIN failed"));
    }
  };

  const closeDetails = () => {
    setDetailsTarget(null);
    setDetailsPinReveal(null);
    setDetailsPinLoading(false);
    setDetailsEditMode(false);
    setEditForm(emptyEditForm());
    setOwnerFieldsHydrated(false);
    setActionError(null);
    setResolveTarget(null);
    setResolutionNote("");
    setResolveError(null);
    // Client Support deep-links leave ?client=&panel=support in the URL.
    // Clearing them on dismiss prevents the deep-link effect from immediately
    // re-opening the modal (and from surviving a refresh).
    // Do NOT clear appliedDeepLinkRef here — clearing it before setSearchParams
    // commits would allow the effect to re-open while ?client= is still sticky.
    // The effect resets the ref when clientId is absent.
    const { next, changed } = stripAccountsDeepLinkParams(searchParams);
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  };

  const openDetails = async (
    account: MasterAccountRow,
    opts?: { keepEditMode?: boolean },
  ) => {
    setDetailsTarget(account);
    setDetailsPinReveal(null);
    if (!opts?.keepEditMode) {
      setDetailsEditMode(false);
      setEditForm(emptyEditForm());
      setOwnerFieldsHydrated(false);
      setActionError(null);
    }
    setDetailsPinLoading(true);
    try {
      const result = await fetchSecurityPin(account.masterAccountId).unwrap();
      setDetailsPinReveal({
        pinStatus: result.pinStatus,
        viewable: result.viewable,
        securityPin: result.securityPin,
        message: result.message,
      });
    } catch (err: unknown) {
      // Reveal API may be undeployed; fall back to list pinStatus without a
      // raw gateway "Not Found" string in the modal body.
      const status = (err as { status?: number })?.status;
      const soft =
        status === 404
          ? undefined
          : pinApiErrorMessage(err, "Unable to load security PIN.");
      setDetailsPinReveal({
        pinStatus: account.pinStatus,
        viewable: false,
        securityPin: null,
        message: soft,
      });
    } finally {
      setDetailsPinLoading(false);
    }
  };

  const closeResolveModal = () => {
    setResolveTarget(null);
    setResolutionNote("");
    setResolveError(null);
  };

  const openResolveModal = (ticket: SupportTicketRow) => {
    setResolveTarget(ticket);
    setResolutionNote("");
    setResolveError(null);
  };

  const closeIntoCaseModal = () => {
    setIntoCaseTarget(null);
    setIntoCaseReason("");
    setIntoCaseMinutes(30);
    setIntoCaseError(null);
  };

  const openIntoCaseModal = (ticket: SupportTicketRow) => {
    setIntoCaseTarget(ticket);
    setIntoCaseReason(
      `Investigate Support Case ${ticket.ticketId}${
        ticket.clientCaseLabel ? ` (Case ${ticket.clientCaseLabel})` : ""
      }`,
    );
    setIntoCaseMinutes(30);
    setIntoCaseError(null);
  };

  const handleImpersonateIntoCase = async () => {
    if (!intoCaseTarget || !detailsTarget?.masterAccountId) return;
    if (!detailsTarget.ownerAuth0Sub) {
      setIntoCaseError("This client has no owner membership to impersonate");
      return;
    }
    if (intoCaseReason.trim().length < 10) {
      setIntoCaseError("Reason is required (min 10 characters)");
      return;
    }
    setIntoCaseError(null);
    try {
      const result = await startImpersonation({
        masterAccountId: detailsTarget.masterAccountId,
        userKey: detailsTarget.ownerAuth0Sub,
        reason: intoCaseReason.trim(),
        expectedDurationMinutes: intoCaseMinutes,
        supportTicketId: intoCaseTarget.ticketId,
        preserveSupportCaseStatus: true,
        impersonateIntoCase: true,
        ...(intoCaseTarget.brandId
          ? { brandId: intoCaseTarget.brandId }
          : {}),
      }).unwrap();

      const caseId = result.supportTicketId || intoCaseTarget.ticketId;
      const payload = {
        token: result.token,
        ticketId: result.ticketId,
        expiresAt: result.expiresAt,
        correlationId: result.correlationId,
        masterAccountId: result.masterAccountId,
        userDisplayName: result.userDisplayName,
        userEmail: result.userEmail || null,
        saDisplayName: result.saDisplayName,
        businessName: result.businessName,
        impersonatedUserSub: result.impersonatedUserSub,
        supportTicketId: caseId,
        openSupportTicketId:
          result.openSupportTicketId || caseId,
        targetPath: result.targetPath || "/master-account/support",
        supportModeSource: result.supportModeSource,
        preserveSupportCaseStatus: true,
        impersonateIntoCase: true,
        clientCaseNumber:
          result.supportTicket?.clientCaseNumber ??
          intoCaseTarget.clientCaseNumber ??
          null,
        clientCaseLabel:
          result.supportTicket?.clientCaseLabel ??
          intoCaseTarget.clientCaseLabel ??
          null,
        brandId: result.brandId || intoCaseTarget.brandId || null,
        brandName: result.brandName || intoCaseTarget.brandName || null,
      };
      const encoded = encodeURIComponent(JSON.stringify(payload));
      const url = `${getMaUiOrigin()}/support-mode?sm=${Date.now()}#kk_impersonation=${encoded}`;
      window.open(url, "_blank", "noopener,noreferrer");
      closeIntoCaseModal();
    } catch (err: unknown) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        (err as { message?: string })?.message ||
        "Failed to impersonate into case";
      setIntoCaseError(message);
    }
  };

  /** Close Support Case (Owner lifecycle: Closed is FINAL — no reopen). */
  const handleCloseCase = async () => {
    if (!resolveTarget || !detailsTarget?.masterAccountId) return;
    setResolveError(null);
    try {
      const note = resolutionNote.trim();
      await closeTicket({
        masterAccountId: detailsTarget.masterAccountId,
        ticketId: resolveTarget.ticketId,
        ...(note ? { closureNote: note } : {}),
      }).unwrap();
      closeResolveModal();
    } catch (err: unknown) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        (err as { message?: string })?.message ||
        "Failed to close support case";
      setResolveError(message);
    }
  };

  // Deep-link: /accounts?client=<masterAccountId>&panel=support[&case=KK-######]
  const deepLinkCaseId = parseAccountsDeepLink(searchParams).caseId;
  useEffect(() => {
    const { clientId, panel } = parseAccountsDeepLink(searchParams);
    if (!clientId) {
      appliedDeepLinkRef.current = null;
      return;
    }
    if (!data?.masterAccounts) return;

    const account = data.masterAccounts.find(
      (row) => row.masterAccountId === clientId,
    );
    if (!account) {
      if (view === "archived" && onViewChange) {
        onViewChange("active");
      }
      return;
    }

    const key = `${clientId}:${panel || ""}`;
    // Apply each deep-link key once. Do NOT re-open when the user dismisses
    // the modal while the same query params are still present.
    if (appliedDeepLinkRef.current !== key) {
      appliedDeepLinkRef.current = key;
      void openDetails(account);
    }

    if (panel === "support") {
      const scrollToSupport = () => {
        document
          .getElementById("client-support-cases")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      const t = window.setTimeout(scrollToSupport, 250);
      return () => window.clearTimeout(t);
    }
    return undefined;
    // openDetails is stable enough via account identity; omit to avoid re-open loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data?.masterAccounts, view]);

  /** Open Support Mode — Impersonate for a Client (from inside Client Details). */
  const openImpersonate = (account: MasterAccountRow) => {
    setActionError(null);
    setSupportRefMode("ad-hoc-created");
    setExistingTicketId("");
    setValidatedTicketSummary(null);
    setPersistedSupportCaseId(null);
    setCaseIdReserved(false);
    setImpersonateReason("");
    setImpersonateMinutes(30);
    setImpersonateTarget(account);
    void reserveCaseIdForTarget(account);
  };

  const resetImpersonateForm = () => {
    setImpersonateTarget(null);
    setImpersonateReason("");
    setImpersonateMinutes(30);
    setSupportRefMode("ad-hoc-created");
    setExistingTicketId("");
    setTicketSearch("");
    setValidatedTicketSummary(null);
    setPersistedSupportCaseId(null);
    setCaseIdReserved(false);
    setSelectedBrandId("");
    setLockedCaseBrandId(null);
    setLockedCaseBrandName(null);
  };

  const reserveCaseIdForTarget = async (account: MasterAccountRow) => {
    setPersistedSupportCaseId(null);
    setCaseIdReserved(false);
    setActionError(null);
    try {
      const result = await reserveSupportTicket({
        masterAccountId: account.masterAccountId,
      }).unwrap();
      const caseId = result.supportTicket?.ticketId;
      if (!caseId) {
        setActionError("Failed to reserve a Case ID");
        return;
      }
      setPersistedSupportCaseId(caseId);
      setCaseIdReserved(true);
    } catch (err: any) {
      setPersistedSupportCaseId(null);
      setCaseIdReserved(false);
      setActionError(
        err?.data?.message || err?.message || "Failed to reserve Case ID",
      );
    }
  };

  const handleValidateTicket = async (ticketIdOverride?: string) => {
    if (!impersonateTarget) return;
    const ticketId = String(ticketIdOverride || existingTicketId || "").trim();
    if (!ticketId) return;
    setActionError(null);
    setValidatedTicketSummary(null);
    setPersistedSupportCaseId(null);
    setCaseIdReserved(false);
    setLockedCaseBrandId(null);
    setLockedCaseBrandName(null);
    setExistingTicketId(ticketId);
    try {
      const result = await validateSupportTicket({
        masterAccountId: impersonateTarget.masterAccountId,
        ticketId,
      }).unwrap();
      const caseId = result.supportTicket.ticketId;
      setValidatedTicketSummary(
        `${caseId} — ${result.supportTicket.summary || "No summary"} (${result.supportTicket.status})`,
      );
      setExistingTicketId(caseId);
      setPersistedSupportCaseId(caseId);
      if (result.supportTicket.brandId) {
        setLockedCaseBrandId(result.supportTicket.brandId);
        setLockedCaseBrandName(
          result.supportTicket.brandName || result.supportTicket.brandId,
        );
        setSelectedBrandId(result.supportTicket.brandId);
      }
    } catch (err: any) {
      setValidatedTicketSummary(null);
      setPersistedSupportCaseId(null);
      setLockedCaseBrandId(null);
      setLockedCaseBrandName(null);
      setActionError(
        err?.data?.message || err?.message || "Ticket validation failed",
      );
    }
  };

  const handleCreateSupportCaseAndStart = async () => {
    if (!impersonateTarget?.ownerAuth0Sub) {
      setActionError("This client has no owner membership to impersonate");
      return;
    }
    if (impersonateReason.trim().length < 10) {
      setActionError("Reason is required (min 10 characters)");
      return;
    }
    const isMaintenance = supportRefMode === "maintenance";
    if (isMaintenance && !canLifecycle) {
      setActionError(
        "Super Admin role (or full access) is required for maintenance access",
      );
      return;
    }
    const caseId = persistedSupportCaseId?.trim();
    if (!isMaintenance && !caseId) {
      setActionError(
        supportRefMode === "existing-ticket"
          ? "Enter or select a Case ID, then Validate"
          : "Case ID is not ready yet — wait for reservation",
      );
      return;
    }
    const brandRequired =
      (supportRefMode === "ad-hoc-created" || supportRefMode === "maintenance") &&
      clientBrands.length > 1;
    if (brandRequired && !selectedBrandId) {
      setActionError("Select the Brand to support before continuing");
      return;
    }
    const brandIdForStart =
      supportRefMode === "existing-ticket" && !lockedCaseBrandId
        ? undefined
        : selectedBrandId || undefined;
    setActionError(null);
    try {
      const result = await startImpersonation({
        masterAccountId: impersonateTarget.masterAccountId,
        userKey: impersonateTarget.ownerAuth0Sub,
        reason: impersonateReason,
        expectedDurationMinutes: impersonateMinutes,
        ...(isMaintenance
          ? { maintenanceAccess: true }
          : supportRefMode === "ad-hoc-created"
            ? { reservedSupportTicketId: caseId }
            : { supportTicketId: caseId }),
        ...(brandIdForStart ? { brandId: brandIdForStart } : {}),
      }).unwrap();

      const payload = {
        token: result.token,
        ticketId: result.ticketId,
        expiresAt: result.expiresAt,
        correlationId: result.correlationId,
        masterAccountId: result.masterAccountId,
        userDisplayName: result.userDisplayName,
        userEmail: result.userEmail || null,
        saDisplayName: result.saDisplayName,
        businessName: result.businessName,
        impersonatedUserSub: result.impersonatedUserSub,
        supportTicketId: result.supportTicketId || caseId || null,
        supportModeSource: result.supportModeSource,
        brandId: result.brandId || brandIdForStart || null,
        brandName:
          result.brandName ||
          lockedCaseBrandName ||
          clientBrands.find((b) => b.brandId === brandIdForStart)?.name ||
          null,
      };
      const encoded = encodeURIComponent(JSON.stringify(payload));
      const url = `${getMaUiOrigin()}/support-mode?sm=${Date.now()}#kk_impersonation=${encoded}`;
      window.open(url, "_blank", "noopener,noreferrer");
      resetImpersonateForm();
    } catch (err: any) {
      // If activation succeeded but impersonation failed, case stays open —
      // keep Case ID visible for the employee.
      setActionError(
        err?.data?.message || err?.message || "Failed to create support case",
      );
    }
  };

  const archiveReady =
    !!archiveTarget &&
    !!archivePreflight &&
    archivePreflight.eligible === true &&
    !archivePreflightLoading &&
    canSubmitClientArchive({
      businessName: archiveTarget.businessName,
      nameConfirmation: archiveNameConfirmation,
      reason: archiveReason,
    });
  const pdReady =
    pdTarget != null &&
    pdPreflight != null &&
    canSubmitClientPermanentDelete({
      businessName: pdTarget.businessName,
      nameConfirmation: pdNameConfirmation,
      confirmTyped: pdConfirmTyped,
      reason: pdReason,
      eligible: pdPreflight.eligible,
    });
  const eligibleArtifacts = restoreEligibility?.eligibleArtifacts || [];
  const artifactGroups = groupArtifactsByType(eligibleArtifacts);
  const archiveImpactLines = archiveOperatorImpactLines(archivePreflight);

  if (isLoading) {
    return (
      <div className="py-5 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger">
        {(error as any)?.data?.message ||
          (error as any)?.message ||
          "Failed to load clients"}{" "}
        <Button size="sm" variant="outline-danger" onClick={() => refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <>
      {actionError && (
        <Alert variant="warning" onClose={() => setActionError(null)} dismissible>
          {actionError}
        </Alert>
      )}
      <Table responsive hover>
        <thead>
          <tr>
            <th className={styles.customerNumber}>Customer #</th>
            <th className={styles.companyName}>Company Name</th>
            <th className={styles.companyAddress}>Company Address</th>
            <th className={styles.companyNumber}>Company #</th>
            <th>Brands</th>
            <th>Locations</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((account) => (
            <tr key={account.masterAccountId}>
              <td className={styles.customerNumber}>
                {account.customerNumber || "—"}
              </td>
              <td className={styles.companyName}>
                {account.businessName}
                {findClientQuery && findClientMatches.get(account.masterAccountId)?.length ? (
                  <div
                    className={styles.findClientMatch}
                    data-testid={`find-client-match-${account.masterAccountId}`}
                  >
                    {findClientMatches
                      .get(account.masterAccountId)!
                      .slice(0, 2)
                      .map((m) => (
                        <span key={m.field}>
                          Matched {m.label}: {m.value}
                          {m.field === "ownerName" && m.nameSource === "membership-snapshot"
                            ? " (provisioning snapshot)"
                            : ""}
                        </span>
                      ))}
                  </div>
                ) : null}
              </td>
              <td className={styles.companyAddress}>{account.companyAddress}</td>
              <td className={styles.companyNumber}>
                {account.companyNumber || "—"}
              </td>
              <td>
                <Link
                  to={`/accounts/${encodeURIComponent(account.masterAccountId)}/brands`}
                  aria-label={`View ${account.brandCount} brands for ${account.businessName}`}
                >
                  View {account.brandCount}
                </Link>
              </td>
              <td>
                <Link
                  to={`/accounts/${encodeURIComponent(account.masterAccountId)}/locations`}
                  aria-label={`View ${account.locationCount} locations for ${account.businessName}`}
                >
                  View {account.locationCount}
                </Link>
              </td>
              <td>
                <div className={styles.actionButtons}>
                  <Button
                    variant="link"
                    className={styles.actionLink}
                    title="Client details / Support"
                    aria-label={`Details for ${account.businessName}`}
                    onClick={() => void openDetails(account)}
                  >
                    <i className="bi bi-headset" aria-hidden="true" />
                  </Button>
                  {view === "active" ? (
                    <>
                      {/* Editing, Support Mode and Security PIN actions live inside Client Details. */}
                      {/* Security PIN reset/set lives inside Client Details (key icon next to the PIN label). */}
                      {canLifecycle ? (
                        <Button
                          variant="link"
                          className={styles.archiveLink}
                          title="Archive client"
                          aria-label={`Archive ${account.businessName}`}
                          onClick={() => void openArchiveModal(account)}
                        >
                          <i
                            className={SONG_LIBRARY_ACTION_ICONS.archive}
                            aria-hidden="true"
                          />
                        </Button>
                      ) : null}
                    </>
                  ) : canLifecycle ? (
                    <>
                      <Button
                        variant="link"
                        className={styles.actionLink}
                        title="Restore client"
                        aria-label={`Restore ${account.businessName}`}
                        onClick={() => void openRestoreModal(account)}
                      >
                        <i
                          className={SONG_LIBRARY_ACTION_ICONS.restore}
                          aria-hidden="true"
                        />
                      </Button>
                      <Button
                        variant="link"
                        className={styles.archiveLink}
                        title="Permanently delete client"
                        aria-label={`Permanently delete ${account.businessName}`}
                        onClick={() => void openPdModal(account)}
                      >
                        <i
                          className={
                            SONG_LIBRARY_ACTION_ICONS.permanentDelete
                          }
                          aria-hidden="true"
                        />
                      </Button>
                    </>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
          {pageRows.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                {findClientQuery || filters.masterAccountIds || filters.status
                  ? `No ${view === "archived" ? "archived " : ""}clients match the current filters.`
                  : view === "archived"
                    ? "No archived clients."
                    : "No clients yet. Use Add New Client to provision one."}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <PaginationRow
        page={page}
        onPageChange={(p) => setPage(p)}
        totalPages={totalPages}
        onClickPrevious={() => setPage((p) => Math.max(1, p - 1))}
        handleOnClickNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        disableLeft={page <= 1}
        disableNext={page >= totalPages}
      />

      <Modal
        show={!!detailsTarget}
        onHide={closeDetails}
        size="lg"
        dialogClassName={styles.detailsModalDialog}
        // While Resolve / Impersonate-into-Case is open: keep Client Details
        // mounted, but do not steal focus / Esc / backdrop clicks.
        enforceFocus={
          !resolveTarget && !intoCaseTarget && !pinTarget && !pinSetTarget && !impersonateTarget
        }
        keyboard={
          !resolveTarget && !intoCaseTarget && !pinTarget && !pinSetTarget && !impersonateTarget
        }
        backdrop={
          resolveTarget || intoCaseTarget || pinTarget || pinSetTarget || impersonateTarget
            ? "static"
            : true
        }
      >
        <Modal.Header className={styles.detailsModalHeader}>
          <Modal.Title className={styles.detailsModalTitle}>
            <i
              className={`bi bi-building ${styles.detailsModalTitleIcon}`}
              aria-hidden="true"
            />
            <span>Client Details</span>
            {detailsTarget?.businessName ? (
              <span
                className={styles.detailsModalTitleName}
                data-testid="client-details-title-name"
              >
                : {detailsTarget.businessName}
              </span>
            ) : null}
          </Modal.Title>
          {/*
            Far-right action group: [ Edit | X ]. Both controls keep their slot
            in View + Edit (state changes only) so the header never reflows.
          */}
          <div
            className={styles.detailsHeaderActions}
            data-testid="client-details-header-actions"
          >
            {view === "active" && detailsTarget ? (
              <Button
                variant="link"
                className={[
                  styles.actionLink,
                  styles.detailsEditToggle,
                  detailsEditMode ? styles.detailsEditToggleActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                title={
                  detailsEditMode
                    ? "Editing — click again to exit without saving"
                    : "Edit client"
                }
                aria-label={
                  detailsEditMode
                    ? `Exit edit mode for ${detailsTarget.businessName}`
                    : `Edit ${detailsTarget.businessName}`
                }
                aria-pressed={detailsEditMode}
                onClick={() =>
                  detailsEditMode
                    ? cancelDetailsEdit()
                    : enterDetailsEditMode(detailsTarget)
                }
                data-testid="client-details-enter-edit"
              >
                {/* Icon-only: outline pencil = Edit; filled red pencil = Editing (see aria-label/title). */}
                <i
                  className={detailsEditMode ? "bi bi-pencil-fill" : "bi bi-pencil"}
                  aria-hidden="true"
                />
              </Button>
            ) : null}
            <CloseButton
              className={styles.detailsCloseButton}
              aria-label="Close"
              title={
                detailsEditMode
                  ? "Finish or cancel editing to close"
                  : "Close"
              }
              disabled={Boolean(resolveTarget || intoCaseTarget || detailsEditMode)}
              onClick={closeDetails}
              data-testid="client-details-close"
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          {/*
            ONE canonical Client Details tree for View + Edit. Section order,
            grid, and field positions never change; only each field's VALUE
            control swaps between read-only text and an in-place input.
          */}
          <DetailSection
            title="Company Information"
            icon="bi bi-building"
            data-testid="client-details-company-information"
          >
            <DetailGrid>
              <DetailField icon="bi bi-building" label="Company Name">
                {detailsEditMode ? (
                  <Form.Control
                    size="sm"
                    className={styles.detailInlineInput}
                    value={editForm.businessName}
                    onChange={(e) => setEditField("businessName", e.target.value)}
                    data-testid="client-details-edit-company-name"
                  />
                ) : (
                  detailsTarget?.businessName || "—"
                )}
              </DetailField>
              <DetailField icon="bi bi-hash" label="Customer Number">
                <span data-testid="client-details-customer-number">
                  {detailsTarget?.customerNumber || "—"}
                </span>
              </DetailField>
              <DetailField
                icon="bi bi-calendar3"
                label={view === "archived" ? "Archived On" : "Created"}
              >
                <span data-testid="client-details-created">
                  {formatClientDetailsDate(
                    view === "archived"
                      ? detailsTarget?.deletedAt
                      : detailsTarget?.createdOn || detailsTarget?.createdAt,
                  )}
                </span>
              </DetailField>
              <DetailField
                icon="bi bi-key"
                label="Security PIN"
                labelAction={
                  view === "active" && detailsTarget && !detailsEditMode ? (
                    detailsTarget.pinStatus === "set" ? (
                      <button
                        type="button"
                        className={`${styles.detailFieldPencil} ${styles.detailFieldKey}`}
                        title="Reset security PIN"
                        aria-label={`Reset PIN for ${detailsTarget.businessName}`}
                        disabled={resetting}
                        onClick={() => {
                          setPinTarget(detailsTarget);
                          setPinReason("");
                          setActionError(null);
                        }}
                        data-testid="client-details-reset-pin"
                      >
                        <i className="bi bi-key" aria-hidden="true" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`${styles.detailFieldPencil} ${styles.detailFieldKey}`}
                        title="Set security PIN"
                        aria-label={`Set PIN for ${detailsTarget.businessName}`}
                        disabled={settingPin}
                        onClick={() => {
                          setPinSetTarget(detailsTarget);
                          setPinSetValue("");
                          setPinSetError(null);
                        }}
                        data-testid="client-details-set-pin"
                      >
                        <i className="bi bi-key" aria-hidden="true" />
                      </button>
                    )
                  ) : null
                }
                infoTip={
                  detailsPinReveal?.viewable && detailsPinReveal.securityPin
                    ? "Use this PIN to verify callers. Reset it with the key icon next to this label if needed."
                    : detailsPinReveal?.message && !detailsPinReveal.viewable
                      ? detailsPinReveal.message
                      : !detailsPinReveal?.viewable &&
                          detailsPinReveal?.pinStatus === "set" &&
                          view === "active" &&
                          !detailsPinReveal.message
                        ? "This PIN was stored before phone-verification reveal was enabled. Reset it with the key icon next to this label, then set a new PIN so it can be shown here."
                        : detailsPinReveal?.pinStatus === "unset" &&
                            view === "active"
                          ? "Use the key icon next to this label to set a security PIN."
                          : undefined
                }
              >
                <span data-testid="client-details-security-pin">
                  {detailsPinLoading
                    ? "Loading…"
                    : detailsPinReveal?.viewable && detailsPinReveal.securityPin
                      ? detailsPinReveal.securityPin
                      : detailsPinReveal?.pinStatus === "set"
                        ? "Set (not readable yet)"
                        : "Not Set"}
                </span>
              </DetailField>
              {/* One Address cell in View + Edit: two display lines become stacked inputs in place. */}
              <DetailField icon="bi bi-geo-alt" label="Address">
                {detailsEditMode ? (
                  <div className={styles.detailInlineInputStack}>
                    <Form.Control
                      size="sm"
                      className={styles.detailInlineInput}
                      placeholder="Street"
                      aria-label="Street"
                      value={editForm.address_street}
                      onChange={(e) => setEditField("address_street", e.target.value)}
                      data-testid="client-details-edit-street"
                    />
                    <div className={styles.detailInlineInputRow}>
                      <Form.Control
                        size="sm"
                        className={styles.detailInlineInput}
                        placeholder="City"
                        aria-label="City"
                        value={editForm.address_city}
                        onChange={(e) => setEditField("address_city", e.target.value)}
                        data-testid="client-details-edit-city"
                      />
                      <Form.Control
                        size="sm"
                        className={styles.detailInlineInput}
                        placeholder="ST"
                        aria-label="State"
                        style={{ flex: "0 0 3.2rem" }}
                        value={editForm.address_state}
                        onChange={(e) => setEditField("address_state", e.target.value)}
                        data-testid="client-details-edit-state"
                      />
                    </div>
                    <div className={styles.detailInlineInputRow}>
                      <Form.Control
                        size="sm"
                        className={styles.detailInlineInput}
                        placeholder="ZIP"
                        aria-label="ZIP"
                        value={editForm.address_zipCode}
                        onChange={(e) => setEditField("address_zipCode", e.target.value)}
                        data-testid="client-details-edit-zip"
                      />
                      <Form.Control
                        size="sm"
                        className={styles.detailInlineInput}
                        placeholder="Country"
                        aria-label="Country"
                        value={editForm.address_country}
                        onChange={(e) => setEditField("address_country", e.target.value)}
                        data-testid="client-details-edit-country"
                      />
                    </div>
                  </div>
                ) : (
                  <span data-testid="client-details-address">
                    {formatClientAddressLines(detailsTarget).map((line, idx) => (
                      <React.Fragment key={idx}>
                        {idx > 0 ? <br /> : null}
                        {line}
                      </React.Fragment>
                    ))}
                  </span>
                )}
              </DetailField>
              <DetailField icon="bi bi-telephone" label="Company Phone Number">
                {detailsEditMode ? (
                  <Form.Control
                    size="sm"
                    className={styles.detailInlineInput}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(888) 888-8888"
                    value={editForm.billingPhone}
                    onChange={(e) =>
                      setEditField("billingPhone", formatUsPhoneInput(e.target.value))
                    }
                    data-testid="client-details-edit-billing-phone"
                  />
                ) : (
                  formatUsPhoneInput(
                    detailsTarget?.billingPhone || detailsTarget?.companyNumber || "",
                  ) || "—"
                )}
              </DetailField>
              <DetailField icon="bi bi-receipt" label="Billing Email">
                {detailsEditMode ? (
                  <Form.Control
                    size="sm"
                    className={styles.detailInlineInput}
                    type="email"
                    value={editForm.billingEmail}
                    onChange={(e) => setEditField("billingEmail", e.target.value)}
                    data-testid="client-details-edit-billing-email"
                  />
                ) : (
                  detailsTarget?.billingEmail || "—"
                )}
              </DetailField>
              <DetailField icon="bi bi-toggle-on" label="Status">
                {/* Same switch geometry in View (read-only) + Edit (interactive). */}
                <Form.Check
                  type="switch"
                  id="client-details-edit-active"
                  className={styles.detailStatusSwitch}
                  label={
                    (detailsEditMode ? editForm.active : !!detailsTarget?.active)
                      ? "Active"
                      : "Inactive"
                  }
                  checked={detailsEditMode ? editForm.active : !!detailsTarget?.active}
                  disabled={!detailsEditMode}
                  readOnly={!detailsEditMode}
                  onChange={(e) => setEditField("active", e.target.checked)}
                  data-testid="client-details-edit-active"
                />
              </DetailField>
            </DetailGrid>
          </DetailSection>
          {detailsTarget?.masterAccountId ? (
            <OwnerAccountPanel
              masterAccountId={detailsTarget.masterAccountId}
              view={view}
              editMode={detailsEditMode}
              ownerForm={{
                firstName: editForm.ownerFirstName,
                lastName: editForm.ownerLastName,
                phone: editForm.ownerPhone,
              }}
              onOwnerFormChange={setOwnerEditField}
              onOwnerHydrated={handleOwnerHydrated}
              refreshToken={ownerRefreshToken}
            />
          ) : null}
          {detailsTarget?.masterAccountId ? (
            <SupportCasesPanel
              masterAccountId={detailsTarget.masterAccountId}
              clientName={detailsTarget.businessName}
              onRequestClose={openResolveModal}
              onRequestImpersonateIntoCase={openIntoCaseModal}
              deepLinkCaseId={deepLinkCaseId}
              canImpersonate={Boolean(detailsTarget.ownerAuth0Sub)}
              headerAction={
                <>
                  {view === "active" && !detailsEditMode ? (
                    <button
                      type="button"
                      className={styles.detailsSectionAction}
                      title={
                        detailsTarget.ownerAuth0Sub
                          ? "Support Mode — Impersonate this Client"
                          : "Support Mode unavailable — no owner identity yet"
                      }
                      aria-label={`Impersonate ${detailsTarget.businessName}`}
                      disabled={!detailsTarget.ownerAuth0Sub || impersonating}
                      onClick={() => openImpersonate(detailsTarget)}
                      data-testid="client-details-support-mode"
                    >
                      <i className="bi bi-headset" aria-hidden="true" />
                      <span>Support Mode</span>
                    </button>
                  ) : null}
                  {/* Brands MAAL: the existing Brand / user-access management entry
                      point (formerly a body text link at the bottom of this card),
                      promoted to a header action. SAME destination + close behaviour;
                      no MAAL / BrandMembership / authz change. */}
                  <Link
                    className={styles.detailsSectionAction}
                    to={`/accounts/${encodeURIComponent(detailsTarget.masterAccountId)}/brands`}
                    onClick={closeDetails}
                    title="Brands — Master Account Access Level"
                    aria-label={`Brands — Master Account Access Level for ${detailsTarget.businessName}`}
                    data-testid="client-details-manage-brands"
                  >
                    <i className="bi bi-diagram-3" aria-hidden="true" />
                    <span>Brands MAAL</span>
                  </Link>
                </>
              }
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {actionError && detailsEditMode ? (
            <Alert
              variant="warning"
              className="py-2 small w-100 mb-1"
              data-testid="client-details-edit-error"
            >
              {actionError}
            </Alert>
          ) : null}
          {detailsEditMode ? (
            <>
              <Button
                variant="secondary"
                onClick={cancelDetailsEdit}
                data-testid="client-details-cancel-edit"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={
                  updating ||
                  updatingOwner ||
                  !editForm.businessName.trim()
                }
                onClick={() => void handleSaveDetailsEdit()}
                data-testid="client-details-save-edit"
              >
                {updating || updatingOwner ? "Saving…" : "Save"}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={closeDetails}>
              Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!resolveTarget}
        onHide={closeResolveModal}
        // Scoped stack: child backdrop above Client Details, dialog above that.
        // See nestedResolveModalStack.ts / accounts.module.scss.
        className={styles.resolveSupportModal}
        backdropClassName={styles.resolveSupportBackdrop}
        style={{ zIndex: NESTED_RESOLVE_STACK.childModalZIndex }}
        enforceFocus
        restoreFocus
        keyboard
      >
        <Modal.Header closeButton>
          <Modal.Title>Close Support Case</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            <strong className="font-monospace">{resolveTarget?.ticketId}</strong>
          </p>
          <p className="small mb-1">
            <span className="text-muted">Client:</span>{" "}
            {detailsTarget?.businessName ||
              resolveTarget?.businessName ||
              "—"}
          </p>
          {resolveTarget?.brandName ? (
            <p className="small mb-1">
              <span className="text-muted">Brand:</span> {resolveTarget.brandName}
            </p>
          ) : null}
          <p className="small mb-2">
            <span className="text-muted">Status:</span>{" "}
            {formatSupportStatusLabel(resolveTarget?.status)}
          </p>
          <div className="mb-3">
            <div
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: 11 }}
            >
              Support request
            </div>
            <div className="small" style={{ whiteSpace: "pre-wrap" }}>
              {resolveTarget?.summary || "—"}
            </div>
          </div>
          {resolveError ? (
            <Alert variant="warning" className="py-2">
              {resolveError}
            </Alert>
          ) : null}
          <Form.Group>
            <Form.Label>Closure note (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              placeholder="Short note for the Client history (how it was handled)"
              maxLength={2000}
            />
          </Form.Group>
          <p className="small text-muted mt-2 mb-0">
            Closes this Support Case without starting Support Mode. Closed is
            final: the conversation stays readable for the Client, no further
            replies are accepted (email replies receive closed-case guidance),
            the Client is emailed a Case-closed notice with a satisfaction
            survey, and any further help requires a new Support Case.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeResolveModal}
            disabled={closingTicket}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={closingTicket || !resolveTarget}
            onClick={() => void handleCloseCase()}
            data-testid="confirm-close-support-case"
          >
            {closingTicket ? "Closing…" : "Close Support Case"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!intoCaseTarget}
        onHide={closeIntoCaseModal}
        className={styles.resolveSupportModal}
        backdropClassName={styles.resolveSupportBackdrop}
        style={{ zIndex: NESTED_RESOLVE_STACK.childModalZIndex }}
        enforceFocus
        restoreFocus
        keyboard
      >
        <Modal.Header closeButton>
          <Modal.Title>Impersonate into Case</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">
            <strong>
              {intoCaseTarget
                ? formatClientCaseHeading(intoCaseTarget)
                : "Case"}
            </strong>
          </p>
          <p className="mb-2">
            <span className="text-muted">Case Number:</span>{" "}
            <strong className="font-monospace">
              {intoCaseTarget?.ticketId}
            </strong>
          </p>
          <p className="small mb-1">
            <span className="text-muted">Client:</span>{" "}
            {detailsTarget?.businessName || intoCaseTarget?.businessName || "—"}
          </p>
          <p className="small mb-2">
            <span className="text-muted">Status:</span>{" "}
            {formatSupportStatusLabel(intoCaseTarget?.status)}
          </p>
          {isClosedSupportStatus(intoCaseTarget?.status) ? (
            <Alert variant="info" className="py-2 small">
              This Support Case is closed. Impersonation opens the historical
              conversation read-only and never reopens the Case.
            </Alert>
          ) : null}
          {intoCaseError ? (
            <Alert variant="warning" className="py-2">
              {intoCaseError}
            </Alert>
          ) : null}
          <Form.Group className="mb-3">
            <Form.Label>Reason (required, min 10 characters)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={intoCaseReason}
              onChange={(e) => setIntoCaseReason(e.target.value)}
              maxLength={500}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Expected duration (minutes)</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={240}
              value={intoCaseMinutes}
              onChange={(e) =>
                setIntoCaseMinutes(Number(e.target.value) || 30)
              }
            />
          </Form.Group>
          <p className="small text-muted mt-2 mb-0">
            Uses the same Super Admin → Master Account Support Mode session.
            You will land in Support with this case conversation open.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeIntoCaseModal}
            disabled={impersonating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              impersonating ||
              !intoCaseTarget ||
              intoCaseReason.trim().length < 10 ||
              !detailsTarget?.ownerAuth0Sub
            }
            onClick={() => void handleImpersonateIntoCase()}
          >
            {impersonating ? "Starting…" : "Impersonate into Case"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!pinTarget}
        onHide={() => setPinTarget(null)}
        // Opened from inside Client Details: use the scoped nested stack so the
        // dark backdrop sits over Client Details (see nestedResolveModalStack.ts).
        className={styles.resolveSupportModal}
        backdropClassName={styles.resolveSupportBackdrop}
        style={{ zIndex: NESTED_RESOLVE_STACK.childModalZIndex }}
        enforceFocus
        restoreFocus
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset security PIN</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Clear the verification PIN for{" "}
            <strong>{pinTarget?.businessName}</strong>. Next you will set a new
            4-digit PIN for caller verification.
          </p>
          <Form.Group>
            <Form.Label>
              Reason for reset (required, min 10 characters)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={pinReason}
              onChange={(e) => setPinReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPinTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={pinReason.trim().length < 10 || resetting}
            onClick={handleResetPin}
          >
            Reset PIN
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!pinSetTarget}
        onHide={closeSetPin}
        className={styles.resolveSupportModal}
        backdropClassName={styles.resolveSupportBackdrop}
        style={{ zIndex: NESTED_RESOLVE_STACK.childModalZIndex }}
        enforceFocus
        restoreFocus
      >
        <Modal.Header closeButton>
          <Modal.Title>Set security PIN</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Set a 4-digit verification PIN for{" "}
            <strong>{pinSetTarget?.businessName}</strong>. It will show in Client
            details so Super Admins can verify callers.
          </p>
          {pinSetError ? (
            <Alert variant="warning" className="py-2">
              {pinSetError}
            </Alert>
          ) : null}
          <Form.Group>
            <Form.Label>Security PIN (4 digits)</Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              placeholder="e.g. 1234"
              value={pinSetValue}
              onChange={(e) =>
                setPinSetValue(e.target.value.replace(/\D/g, "").slice(0, 4))
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSetPin}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={settingPin || !/^\d{4}$/.test(pinSetValue.trim())}
            onClick={() => void handleSetPin()}
          >
            {settingPin ? "Saving…" : "Set PIN"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!impersonateTarget}
        onHide={resetImpersonateForm}
        // Opened from inside Client Details: scoped nested stack (dark backdrop over Client Details).
        className={`${styles.resolveSupportModal} ${styles.compactModal}`}
        backdropClassName={styles.resolveSupportBackdrop}
        style={{ zIndex: NESTED_RESOLVE_STACK.childModalZIndex }}
        enforceFocus
        restoreFocus
      >
        <Modal.Header closeButton>
          <Modal.Title className={styles.detailsModalTitle}>
            <i
              className={`bi bi-headset ${styles.detailsModalTitleIcon}`}
              aria-hidden="true"
            />
            <span>Support Mode — Impersonate</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Enter <strong>{impersonateTarget?.businessName}</strong>
            {formatOwnerLabel(
              impersonateTarget?.ownerDisplayName,
              impersonateTarget?.ownerEmail,
            )
              ? ` as ${formatOwnerLabel(
                  impersonateTarget?.ownerDisplayName,
                  impersonateTarget?.ownerEmail,
                )}`
              : ""}
            . Support sessions should reference a real support case whenever
            possible.
          </p>
          {actionError && (
            <Alert variant="danger" className="py-2">
              {actionError}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Support reference</Form.Label>
            <Form.Check
              type="radio"
              id="support-ref-existing"
              name="supportRefMode"
              label="Use existing support ticket"
              checked={supportRefMode === "existing-ticket"}
              onChange={() => {
                setSupportRefMode("existing-ticket");
                setValidatedTicketSummary(null);
                setPersistedSupportCaseId(null);
                setCaseIdReserved(false);
                setSelectedBrandId("");
                setLockedCaseBrandId(null);
                setLockedCaseBrandName(null);
                setTicketSearch("");
                setActionError(null);
              }}
            />
            {supportRefMode === "existing-ticket" && (
              <div className="ms-4 mt-2 mb-2">
                <Form.Label className="small">Type Case ID</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    size="sm"
                    value={existingTicketId}
                    onChange={(e) => {
                      setExistingTicketId(e.target.value);
                      setValidatedTicketSummary(null);
                      setPersistedSupportCaseId(null);
                      setLockedCaseBrandId(null);
                      setLockedCaseBrandName(null);
                    }}
                    placeholder="KK-123456"
                    data-testid="support-ref-ticket-id"
                  />
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className={styles.detailsOutlineBtn}
                    disabled={
                      !existingTicketId.trim() ||
                      validatingTicket ||
                      !impersonateTarget
                    }
                    onClick={() => void handleValidateTicket()}
                  >
                    {validatingTicket ? "…" : "Validate"}
                  </Button>
                </div>
                <Form.Label className="small mt-3 mb-1">
                  Or search open tickets
                </Form.Label>
                <Form.Control
                  className="mb-2"
                  value={ticketSearch}
                  onChange={(e) => setTicketSearch(e.target.value)}
                  placeholder="Search by Case ID, label, or summary…"
                  data-testid="support-ref-ticket-search"
                />
                {openTicketsLoading ? (
                  <div className="small text-muted">Loading open tickets…</div>
                ) : openSupportTickets.length === 0 ? (
                  <div className="small text-muted">
                    No open tickets for this Client
                    {ticketSearch.trim() ? " match that search" : ""}.
                  </div>
                ) : (
                  <Form.Select
                    value={
                      openSupportTickets.some(
                        (t) => t.ticketId === existingTicketId.trim(),
                      )
                        ? existingTicketId.trim()
                        : ""
                    }
                    onChange={(e) => {
                      const id = e.target.value;
                      setExistingTicketId(id);
                      setValidatedTicketSummary(null);
                      setPersistedSupportCaseId(null);
                      setLockedCaseBrandId(null);
                      setLockedCaseBrandName(null);
                      if (id) {
                        void handleValidateTicket(id);
                      }
                    }}
                    data-testid="support-ref-open-ticket-select"
                  >
                    <option value="">Select an open ticket…</option>
                    {openSupportTickets.map((t) => (
                      <option key={t.ticketId} value={t.ticketId}>
                        {t.ticketId}
                        {t.clientCaseNumber != null
                          ? ` · ${formatClientCaseHeading(t)}`
                          : ""}
                        {t.summary ? ` — ${t.summary}` : ""}
                      </option>
                    ))}
                  </Form.Select>
                )}
                {persistedSupportCaseId && (
                  <div className="mt-2" data-testid="persisted-support-case">
                    <div className="d-flex align-items-baseline gap-2">
                      <span className="text-uppercase small text-muted">
                        Case ID
                      </span>
                      <span
                        className="fw-semibold font-monospace user-select-all"
                        style={{ fontSize: "1rem" }}
                      >
                        {persistedSupportCaseId}
                      </span>
                    </div>
                  </div>
                )}
                {validatedTicketSummary && !persistedSupportCaseId && (
                  <div className="small text-success mt-1">
                    {validatedTicketSummary}
                  </div>
                )}
              </div>
            )}
            <Form.Check
              type="radio"
              id="support-ref-adhoc"
              name="supportRefMode"
              label="Create Support Case Now"
              className="mt-2"
              checked={supportRefMode === "ad-hoc-created"}
              onChange={() => {
                setSupportRefMode("ad-hoc-created");
                setValidatedTicketSummary(null);
                setExistingTicketId("");
                setTicketSearch("");
                setLockedCaseBrandId(null);
                setLockedCaseBrandName(null);
                setActionError(null);
                if (impersonateTarget) {
                  void reserveCaseIdForTarget(impersonateTarget);
                }
              }}
            />
            {supportRefMode === "ad-hoc-created" && (
              <div className="ms-4 mt-2" data-testid="persisted-support-case">
                {reservingCaseId && !persistedSupportCaseId ? (
                  <div className="small text-muted">Reserving Case ID…</div>
                ) : persistedSupportCaseId && caseIdReserved ? (
                  <>
                    <div className="d-flex align-items-baseline gap-2">
                      <span className="text-uppercase small text-muted">
                        Case ID
                      </span>
                      <span
                        className="fw-semibold font-monospace user-select-all"
                        style={{ fontSize: "1rem" }}
                      >
                        {persistedSupportCaseId}
                      </span>
                    </div>
                    <div className="small text-muted mt-1">
                      Reserved by KrossKast. Becomes active when you click Create
                      Support Case.
                    </div>
                  </>
                ) : (
                  <div className="small text-muted">
                    Unable to reserve a Case ID. Close and try again.
                  </div>
                )}
              </div>
            )}
            {canLifecycle ? (
              <>
                <Form.Check
                  type="radio"
                  id="support-ref-maintenance"
                  name="supportRefMode"
                  label="Maintenance access (no Support Ticket)"
                  className="mt-2"
                  checked={supportRefMode === "maintenance"}
                  data-testid="support-ref-maintenance"
                  onChange={() => {
                    setSupportRefMode("maintenance");
                    setValidatedTicketSummary(null);
                    setExistingTicketId("");
                    setTicketSearch("");
                    setPersistedSupportCaseId(null);
                    setCaseIdReserved(false);
                    setLockedCaseBrandId(null);
                    setLockedCaseBrandName(null);
                    setActionError(null);
                  }}
                />
                {supportRefMode === "maintenance" ? (
                  <div className="ms-4 mt-2">
                    <Alert variant="warning" className="py-2 mb-0 small">
                      Super Admin only. Enters the Client account without
                      creating, linking, or notifying a Support Ticket. Still
                      audited as impersonation. Use for checks and maintenance
                      only.
                    </Alert>
                  </div>
                ) : null}
              </>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Brand to support</Form.Label>
            {clientBrandsLoading ? (
              <div className="small text-muted">Loading brands…</div>
            ) : lockedCaseBrandId ? (
              <>
                <Form.Control
                  value={lockedCaseBrandName || lockedCaseBrandId}
                  disabled
                  readOnly
                />
                <div className="small text-muted mt-1">
                  From the existing support case — landing context is locked.
                </div>
              </>
            ) : supportRefMode === "existing-ticket" ? (
              <>
                <Form.Control value="Client / Brand Manager" disabled readOnly />
                <div className="small text-muted mt-1">
                  This case has no Brand association. Landing stays Client-level.
                </div>
              </>
            ) : clientBrands.length === 0 ? (
              <>
                <Form.Control value="Client / Brand Manager" disabled readOnly />
                <div className="small text-muted mt-1">
                  This Client has no Brands. Support Mode lands at Brand Manager
                  (Client-level).
                </div>
              </>
            ) : clientBrands.length === 1 ? (
              <>
                <Form.Control value={clientBrands[0].name} disabled readOnly />
                <div className="small text-muted mt-1">
                  Only Brand for this Client — selected automatically.
                </div>
              </>
            ) : (
              <Form.Select
                value={selectedBrandId}
                onChange={(e) => setSelectedBrandId(e.target.value)}
                data-testid="support-mode-brand-select"
              >
                <option value="">Select a Brand…</option>
                {clientBrands.map((b) => (
                  <option key={b.brandId} value={b.brandId}>
                    {b.name}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              {supportRefMode === "maintenance"
                ? "What are you checking or maintaining?"
                : "What does the client need help with?"}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={impersonateReason}
              onChange={(e) => setImpersonateReason(e.target.value)}
              placeholder={
                supportRefMode === "maintenance"
                  ? "Describe the maintenance or check (min 10 characters)"
                  : "What does the client need help with?"
              }
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Expected duration (minutes, max 240)</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={240}
              value={impersonateMinutes}
              onChange={(e) =>
                setImpersonateMinutes(Number(e.target.value) || 30)
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetImpersonateForm}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={
              impersonateReason.trim().length < 10 ||
              impersonating ||
              reservingCaseId ||
              !impersonateTarget?.ownerAuth0Sub ||
              (supportRefMode !== "maintenance" && !persistedSupportCaseId) ||
              (supportRefMode === "ad-hoc-created" && !caseIdReserved) ||
              ((supportRefMode === "ad-hoc-created" ||
                supportRefMode === "maintenance") &&
                clientBrands.length > 1 &&
                !selectedBrandId) ||
              (supportRefMode === "maintenance" && !canLifecycle)
            }
            onClick={handleCreateSupportCaseAndStart}
            data-testid="start-support-mode"
          >
            {impersonating
              ? "Starting…"
              : supportRefMode === "ad-hoc-created"
                ? "Create Support Case"
                : supportRefMode === "maintenance"
                  ? "Start Maintenance Access"
                  : "Start Support Mode"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!archiveTarget}
        onHide={closeArchiveModal}
        data-testid="client-archive-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Archive &quot;{archiveTarget?.businessName}&quot;?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {archiveError && <Alert variant="danger">{archiveError}</Alert>}
          {archivePreflightLoading && (
            <div
              className="text-center py-3"
              data-testid="client-archive-preflight-loading"
            >
              <Spinner animation="border" size="sm" className="me-2" />
              Calculating Archive impact for this Client…
            </div>
          )}
          {!archivePreflightLoading && archivePreflight && (
            <Alert
              variant="warning"
              className={styles.archiveImpactAlert}
              data-testid="client-archive-impact"
            >
              <div className={styles.archiveWarningTitle}>
                WARNING — CLIENT ACCESS WILL BE REMOVED
              </div>
              <p className="mb-1 fw-semibold">Archiving this Client will:</p>
              <ul
                className="mb-3 ps-3"
                data-testid="client-archive-blast-radius"
              >
                {archiveImpactLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <p className="mb-2">
                The Client&apos;s Brands, Locations, Zones, Devices, and device
                playback will not be deleted.
              </p>
              <p className="mb-0">You can restore this Client later.</p>
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>
              Type the company name to confirm (
              {archiveTarget?.businessName})
            </Form.Label>
            <Form.Control
              value={archiveNameConfirmation}
              onChange={(e) => setArchiveNameConfirmation(e.target.value)}
              autoComplete="off"
              disabled={
                archivePreflightLoading ||
                !archivePreflight ||
                archivePreflight.eligible !== true
              }
              data-testid="client-archive-name-confirmation"
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>
              Reason (min {CLIENT_LIFECYCLE_MIN_REASON_LENGTH} characters)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Why is this Client being archived?"
              disabled={
                archivePreflightLoading ||
                !archivePreflight ||
                archivePreflight.eligible !== true
              }
              data-testid="client-archive-reason"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeArchiveModal}
            disabled={archiving}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!archiveReady || archiving}
            onClick={() => void handleArchive()}
            data-testid="client-archive-submit"
          >
            {archiving ? "Archiving…" : "Archive Client"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!restoreTarget}
        onHide={closeRestoreModal}
        size="lg"
        data-testid="client-restore-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Restore &quot;{restoreTarget?.businessName}&quot;?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {restoreError && <Alert variant="danger">{restoreError}</Alert>}
          <p className="text-muted">
            Choose what access should be restored with this Client.
            Anything left unchecked will remain removed.
          </p>
          {restoreEligibilityLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Loading restore
              options…
            </div>
          ) : restoreEligibilityError ? (
            <Alert variant="danger">
              {extractApiErrorMessage(
                restoreEligibilityErr,
                "Failed to load restore options",
              )}
            </Alert>
          ) : eligibleArtifacts.length === 0 ? (
            <Alert variant="info" className="mb-3">
              No access was removed when this Client was archived. It can
              still be restored without restoring access.
            </Alert>
          ) : (
            <div
              className={styles.artifactList}
              data-testid="client-restore-artifacts"
            >
              {artifactGroups.map((group) => (
                <div key={group.artifactType} className={styles.artifactGroup}>
                  <div className={styles.artifactGroupLabel}>
                    {group.label} ({group.artifacts.length})
                  </div>
                  {group.artifacts.map((artifact) => (
                    <Form.Check
                      key={artifact.artifactId}
                      type="checkbox"
                      id={`restore-a-${artifact.artifactId}`}
                      className={styles.artifactCheck}
                      checked={selectedArtifactIds.includes(
                        artifact.artifactId,
                      )}
                      onChange={(e) =>
                        setSelectedArtifactIds((prev) =>
                          toggleArtifactSelection(
                            prev,
                            artifact.artifactId,
                            e.target.checked,
                          ),
                        )
                      }
                      label={formatClientArtifactLabel(artifact)}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
          <Form.Group className="mb-3 mt-3">
            <Form.Label>Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={restoreNotes}
              onChange={(e) => setRestoreNotes(e.target.value)}
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            id="client-restore-confirm"
            checked={restoreConfirmed}
            onChange={(e) => setRestoreConfirmed(e.target.checked)}
            label="I confirm restoring this Client with the access selected above."
            data-testid="client-restore-confirm"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeRestoreModal}
            disabled={restoring}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={
              !restoreConfirmed ||
              restoring ||
              restoreEligibilityLoading ||
              restoreEligibilityError
            }
            onClick={() => void handleRestore()}
            data-testid="client-restore-submit"
          >
            {restoring ? "Restoring…" : "Restore Client"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!pdTarget}
        onHide={closePdModal}
        data-testid="client-permanent-delete-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Permanently delete &quot;{pdTarget?.businessName}&quot;?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pdPhase === "preflight" ? (
            <div className="text-muted mb-3">Loading dependency summary…</div>
          ) : null}
          {pdError && <Alert variant="danger">{pdError}</Alert>}
          {pdPreflight ? (
            <div className="mb-3">
              <strong>Will remove</strong>
              <ul>
                {(pdPreflight.counts || [])
                  .filter((c) => c.value != null)
                  .map((c) => (
                    <li key={c.label}>
                      {c.label}: {c.value}
                    </li>
                  ))}
                {(pdPreflight.willRemove || []).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <strong>Will retain forever</strong>
              <ul>
                {(pdPreflight.willRetain || []).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {!pdPreflight.eligible ? (
                <Alert
                  variant="warning"
                  className="mb-0"
                  data-testid="client-pd-blockers"
                >
                  Deletion blocked:{" "}
                  {(pdPreflight.blockers || [])
                    .map((b) => b.message)
                    .join(" ") || "Not eligible for permanent delete."}
                </Alert>
              ) : null}
            </div>
          ) : null}

          {pdPreflight?.eligible ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>
                  Type the company name to confirm ({pdTarget?.businessName})
                </Form.Label>
                <Form.Control
                  value={pdNameConfirmation}
                  onChange={(e) => setPdNameConfirmation(e.target.value)}
                  autoComplete="off"
                  disabled={deleting || pdPhase === "running"}
                  data-testid="client-pd-name-confirmation"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  Type {CLIENT_PERMANENT_DELETE_CONFIRM_TOKEN} to confirm
                </Form.Label>
                <Form.Control
                  value={pdConfirmTyped}
                  onChange={(e) => setPdConfirmTyped(e.target.value)}
                  autoComplete="off"
                  disabled={deleting || pdPhase === "running"}
                  data-testid="client-pd-confirm-token"
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>
                  Reason (min {CLIENT_LIFECYCLE_MIN_REASON_LENGTH} characters)
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={pdReason}
                  onChange={(e) => setPdReason(e.target.value)}
                  disabled={deleting || pdPhase === "running"}
                  placeholder="Why is this Client being permanently deleted?"
                  data-testid="client-pd-reason"
                />
              </Form.Group>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closePdModal}
            disabled={deleting || pdPhase === "running"}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={
              !pdReady ||
              deleting ||
              pdPhase === "preflight" ||
              pdPhase === "running"
            }
            onClick={() => void handlePermanentDelete()}
            data-testid="client-pd-submit"
          >
            {pdPhase === "running" || deleting
              ? "Deleting…"
              : "Permanently Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AccountsList;
